import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import Rollbar from 'utils/rollbar'
import { isAuth0Timeout, isRoleAllowed, saveCustomerIdFromCustomerAuth } from 'utils/auth'
import {
  fetchCurrentUser,
  saveAuthToken,
  terminateSession,
  revokeFactSetIdpSession,
} from 'services/auth'
import { factset as factsetConfig } from 'config'

import { ForbiddenError } from 'constants/errors'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import {
  CUSTOMER_ID,
  JUST_LOGGED_OUT_KEY,
  JUST_SESSION_EXPIRED_KEY,
  NEW_AUTH_KEY,
} from 'constants/localStorage'

const TOKEN_ERRORS = ['login_required', 'consent_required']

// This token is only set up to minimize the impact of having an account
// that has not been properly configured and has a valid authenticated_id present,
// in this case the client cannot determine that the user is invalid (since it receives a 401)
// and will attempt to reauthenticate against the tenant. Since the session is valid
// on auth0 the flow will continue to the API, where the invalid user will once again
// fail to be authenticated. To avoid this infinite redirect loop, we set a control flag and report
// that a problem was encountered.
const AUTH0_AUTH_ATTEMPT_TOKEN = 'auth0-authentication-attempted'
export const auth0AuthAttempted = () => !!localStorage.getItem(AUTH0_AUTH_ATTEMPT_TOKEN)
export const registerAuth0AuthAttempt = () => localStorage.setItem(AUTH0_AUTH_ATTEMPT_TOKEN, true)

function useAuth() {
  const [currentUser, setCurrentUser] = useState(null)

  // Must be a lazy useState initializer, NOT a value recomputed on each render.
  // It (1) runs the JUST_LOGGED_OUT_KEY cleanup exactly once on mount instead of on every
  // render, and (2) freezes isSignedOut for the component's lifetime. If we recomputed it
  // inline, a re-render would read it as gone, flip isSignedOut to false, and let
  // fetchAuth0User silently re-auth the user against PingFederate's persistent session —
  // exactly what this guard prevents.
  const [isSignedOut] = useState(() => {
    const wasSignedOut = Boolean(sessionStorage.getItem(JUST_LOGGED_OUT_KEY))
    if (wasSignedOut) {
      sessionStorage.removeItem(JUST_LOGGED_OUT_KEY)
    }
    return wasSignedOut && Boolean(localStorage.getItem(NEW_AUTH_KEY))
  })

  // Same pattern as isSignedOut: read and clear the flag exactly once on mount so that
  // re-renders do not flip it back to false prematurely.
  const [isSessionExpired] = useState(() => {
    const wasSessionExpired = Boolean(sessionStorage.getItem(JUST_SESSION_EXPIRED_KEY))
    if (wasSessionExpired) {
      sessionStorage.removeItem(JUST_SESSION_EXPIRED_KEY)
    }
    return wasSessionExpired && Boolean(localStorage.getItem(NEW_AUTH_KEY))
  })

  const { isLoading, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()

  useEffect(() => {
    async function fetchAuth0User() {
      try {
        if (auth0AuthAttempted()) {
          return
        }

        const token = await getAccessTokenSilently()
        saveAuthToken(token)
        await fetchUser()
      } catch (error) {
        if (TOKEN_ERRORS.includes(error?.error)) {
          if (localStorage.getItem(NEW_AUTH_KEY)) {
            // For FactSet users, show an interstitial instead of silently re-authenticating
            // via PingFederate's persistent session — same guard as the manual-logout flow.
            sessionStorage.setItem(JUST_SESSION_EXPIRED_KEY, 'true')
            window.location.href = window.location.origin
          } else {
            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname,
              },
              redirectUri: window.location.origin,
            })
          }
          return
        }

        // ignore timeout errors to avoid excesive noise if the refresh fails,
        // in most cases this is a false positive due to the auth token being valid
        // for a day
        if (isAuth0Timeout(error)) {
          return
        }

        setCurrentUser(() => {
          throw error
        })
      }
    }

    async function fetchUser() {
      try {
        // we want to clear any customer id previously saved in localStorage before fetching current user again
        // this is to ensure we are always fetching the user with default customer within Admin app
        localStorage.removeItem(CUSTOMER_ID)
        const user = await fetchCurrentUser()

        if (!isRoleAllowed(user)) {
          throw new ForbiddenError({ user })
        }

        saveCustomerIdFromCustomerAuth(user.customerAuthorizations)
        Rollbar.configure({ payload: { person: { id: user.id, email: user.email } } })

        setCurrentUser(user)
      } catch (error) {
        if (error?.response?.data?.errors?.some(e => e.code === AUTH_ERROR_TYPES.identityLocked)) {
          throw error
        }

        if (error?.response?.status === 401) {
          registerAuth0AuthAttempt()
        }

        throw error
      }
    }

    if (!isSignedOut && !isSessionExpired) {
      fetchAuth0User()
    }
  }, [getAccessTokenSilently, loginWithRedirect, isSignedOut, isSessionExpired])

  async function endSession() {
    try {
      await terminateSession()
    } catch {
      // ignore failures terminating the session, this is done optimistically
      // to invalidate the token on our end and issue a new one during the next login
    } finally {
      // Only set the signed-out interstitial flag and clear the FactSet IdP session when
      // the FactSet auth FF is active (indicated by NEW_AUTH_KEY). Both are no-ops for
      // users who have never authenticated via FactSet.
      if (localStorage.getItem(NEW_AUTH_KEY)) {
        sessionStorage.setItem(JUST_LOGGED_OUT_KEY, 'true')

        try {
          await revokeFactSetIdpSession(factsetConfig.authLogoutUrl)
        } catch {
          // ignore — proceed with Auth0 logout regardless
        }
      }

      logout({ returnTo: window.location.origin })
    }
  }

  /**
   * Triggers an Auth0 login redirect, routing through FactSet PingFederate when NEW_AUTH_KEY is set.
   * @returns {void}
   */
  function signIn() {
    loginWithRedirect({
      appState: { returnTo: '/' },
      redirectUri: window.location.origin,
      ...(localStorage.getItem(NEW_AUTH_KEY) && { connection: 'factset' }),
    })
  }

  async function refetchCurrentUser() {
    const user = await fetchCurrentUser()
    setCurrentUser(user)
  }

  return {
    state: { isLoading, currentUser, isSignedOut, isSessionExpired },
    handlers: { endSession, refetchCurrentUser, signIn },
  }
}

export default useAuth
